import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import AddShoppingCartIcon from '@material-ui/icons/AddShoppingCartTwoTone';
import SaveIcon from '@material-ui/icons/SaveTwoTone';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Link as RouterLink } from 'react-router-dom';
import { shoppingCartState } from '../../api/shoppingCart';
import { shoppingListsCreateRequestState, shoppingListsSelectRequestState } from '../../api/shoppingLists';
import useRouteUrlFactory from '../../hooks/useRouteUrlFactory';
import { isAuthenticatedState } from '../../api/appState';
import CartResetButton from '../CartResetButton';
import useTranslateWithFallback from '../../hooks/useTranslateWithFallback';
import ShoppingCartAsideContent from './ShoppingCartAsideContent';
import ShoppingCartAsideEmpty from './ShoppingCartAsideEmpty';
import ShoppingCartAsideTotals from './ShoppingCartAsideTotals';

export type ShoppingCartAsideProps = StandardDivProps<ShoppingCartAsideClassKey>;

export type ShoppingCartAsideClassKey =
  | 'root'
  | 'toolbar'
  | 'subjects'
  | 'emptyCart'
  | 'totals'
  | 'actions'
  | 'redirectButton';

const themeSettings = { name: 'ShoppingCartAside' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<ShoppingCartAsideClassKey, {}>({
      root: {
        display: 'flex',
        flexDirection: 'column',
      },
      toolbar: {
        display: 'flex',
        justifyContent: 'flex-end',
        color: theme.palette.primary.main,
      },
      subjects: {
        flexGrow: 1,
        overflowY: 'auto',
      },
      emptyCart: {},
      totals: {},
      actions: {
        display: 'flex',
        flexDirection: 'column',
        '& button': {
          marginTop: theme.spacing(2),
        },
      },
      redirectButton: {
        backgroundColor: '#002969',
        color: 'white',
        '&:hover': {
          backgroundColor: '#003485',
        },
      },
    }),
  themeSettings,
);

const ShoppingCartAside = React.forwardRef<HTMLDivElement, ShoppingCartAsideProps>(
  ({ className, classes: pClasses }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const cart = useRecoilValue(shoppingCartState);
    const t = useTranslateWithFallback();

    const shoppingListSaveDialogOpen = useSetRecoilState(shoppingListsCreateRequestState);
    const shoppingListSelectDialogOpen = useSetRecoilState(shoppingListsSelectRequestState);
    const isAuthenticated = useRecoilValue(isAuthenticatedState);
    const routeUrlFactory = useRouteUrlFactory();

    if (!isAuthenticated) {
      return (
        <div ref={ref} className={clsx(classes.root, className)}>
          <div className={classes.actions}>
            <Button
              component={RouterLink}
              to={routeUrlFactory('homePage')}
              className={classes.redirectButton}
              size='large'
              variant='contained'
            >
              {t('loginPage/login')}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div ref={ref} className={clsx(classes.root, className)}>
        <div className={classes.toolbar}>
          <CartResetButton iconOnly />
          <IconButton color='inherit' onClick={() => shoppingListSaveDialogOpen(true)}>
            <SaveIcon />
          </IconButton>
          <IconButton color='inherit' onClick={() => shoppingListSelectDialogOpen(true)}>
            <AddShoppingCartIcon />
          </IconButton>
        </div>
        {!cart ||
          !cart.cartContent ||
          (cart.cartContent.subjects.length === 0 && (
            <ShoppingCartAsideEmpty className={clsx(classes.subjects, classes.emptyCart)} />
          ))}
        {cart && cart.cartContent && cart.cartContent.subjects.length > 0 && (
          <ShoppingCartAsideContent className={classes.subjects} cartContent={cart.cartContent} />
        )}
        <ShoppingCartAsideTotals className={classes.totals} />
      </div>
    );
  },
);

ShoppingCartAside.displayName = 'ShoppingCartAside';

export default withThemeProps(themeSettings)(ShoppingCartAside);
